import AppLoader from "../components/AppLoader";
import { useAuth } from "../hooks/AuthContext";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Fade,
  Grow,
  Link,
  Typography,
  Avatar,
} from "@mui/material";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import { useTranslation } from "react-i18next";
import { api } from "../services/api";
import AppBackground from "../components/layout/AppBackground";

const Pricing = () => {
  const { t, i18n } = useTranslation("pricing");
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const { isAuthenticated, user, authLoading } = useAuth();
  const location = useLocation();
  const isUndeltaUser = user?.source === "undelta";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/api/pricing");
        setPlans(res.data.plans || []);
        setPacks(res.data.packs || []);

        const sub = res.data.user_subscription;
        const isValid = sub && new Date(sub.end_date) > new Date();
        setActiveSubscription(isValid ? sub : null);
      } catch (error) {
        console.error("Failed to load pricing:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [lang]);

  useEffect(() => {
    if (!loading && location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    }
  }, [location, loading]);

  if (authLoading) {
    return <AppLoader message={t("loading")} />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (!user?.is_early_pitcher && !isUndeltaUser) {
    return <Navigate to="/early-pitchers" replace />;
  }

  const isEarlyFounder = (plan) => {
    const name = (plan.name_en || "").toLowerCase();
    return name.includes("early");
  };

  const renderPlan = (plan) => {
    const isActive = activeSubscription && activeSubscription.plan_id === plan.id;
    const isDisabled = isActive || isUndeltaUser;

    const monthly = (plan.price_usd / plan.duration_months).toFixed(2);
    const billedQuarterly = t("billed_quarterly", { amount: `$${plan.price_usd}` });
    const highlight = isEarlyFounder(plan);

    return (
      <Box
        key={plan.id}
        sx={{
          backgroundColor: highlight ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.45)",
          color: "white",
          borderRadius: 4,
          boxShadow: highlight ? "0 0 20px rgba(255,185,80,0.5)" : 3,
          border: highlight ? "1.5px solid #ffb950" : "1px solid rgba(255,255,255,0.2)",
          transform: highlight ? "scale(1.03)" : "none",
          transition: "all 0.25s ease",
          p: 4,
          m: 1,
          maxWidth: 320,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {isActive && (
          <Chip
            label={t("active_plan")}
            sx={{
              borderRadius: 3,
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: "#ec9e50ff",
              fontWeight: "bold",
              color: "white",
            }}
          />
        )}

        {highlight && !isActive && (
          <Chip
            label={t("early_access")}
            sx={{
              position: "absolute",
              top: -14,
              right: 16,
              backgroundColor: "#ffcc7a",
              color: "#3a2a00",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              zIndex: 2,
            }}
          />
        )}

        <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: "bold" }}>
          {lang === "es" ? plan.name_es : plan.name_en}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          ${monthly}{t("per_month")}
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: "italic", mb: 2 }}>
          {billedQuarterly}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            pb: {
              xs: 2,
              md: 1,
            },
          }}
        >
          {lang === "es" ? plan.description_es : plan.description_en}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) navigate(`/checkout?type=plan&id=${plan.id}`);
          }}
          sx={{
            mt: 3,
            borderRadius: 999,
            backgroundColor: highlight ? "#ffb950" : undefined,
            color: highlight ? "black" : undefined,
            "&:hover": highlight ? { backgroundColor: "#ffc46b" } : undefined,
          }}
        >
          {isActive ? t("current_plan") : t("choose")}
        </Button>
      </Box>
    );
  };

  const renderPack = (pack) => {
    const canBuyPack = !!activeSubscription && !isUndeltaUser;
    const isDisabled = !canBuyPack;

    return (
      <Box
        key={pack.id}
        sx={{
          flex: 1,
          minWidth: "240px",
          maxWidth: 320,
          backgroundColor: "rgba(0,0,0,0.5)",
          color: "white",
          borderRadius: 4,
          px: 3,
          py: 4,
          m: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
          {lang === "es" ? pack.name_es : pack.name_en}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {lang === "es" ? pack.description_es : pack.description_en}
        </Typography>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          ${pack.price_usd}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) navigate(`/checkout?type=pack&id=${pack.id}`);
          }}
          sx={{
            mt: 1,
            borderRadius: 999,
            pointerEvents: isDisabled ? "none" : "auto",
          }}
        >
          {t("buy_pack")}
        </Button>
      </Box>
    );
  };

  if (loading) return <AppLoader message={t("loading")} />;

  return (
    <Fade in timeout={700}>
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          justifyContent: {
            xs: "center",
            md: "flex-start",
          },
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" gutterBottom sx={{ color: "white" }}>
            {t("title")}
          </Typography>

          {isUndeltaUser && (
            <Box
              sx={{
                mb: 3,
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg, rgba(44, 24, 74, 0.96) 0%, rgba(63, 28, 105, 0.92) 55%, rgba(30, 17, 57, 0.96) 100%)",
                  borderRadius: 4,
                  boxShadow: "0 18px 50px rgba(0,0,0,0.28)",
                  p: 3,
                  border: "1px solid rgba(168, 85, 247, 0.35)",
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 0, sm: 2 },
                  color: "white",
                }}
              >
                <Avatar
                  src="/images/ai_profiles/tincho.jpg"
                  alt="Tincho"
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    width: 50,
                    height: 50,
                    border: "3px solid rgba(250, 204, 21, 0.95)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.28)",
                    flexShrink: 0,
                    alignSelf: "flex-start",
                    mt: 0.25,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                      <Avatar
                        src="/images/ai_profiles/tincho.jpg"
                        alt="Tincho"
                        sx={{
                          display: { xs: "flex", sm: "none" },
                          width: 42,
                          height: 42,
                          border: "3px solid rgba(250, 204, 21, 0.95)",
                          boxShadow: "0 12px 24px rgba(0,0,0,0.28)",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 0,
                          fontWeight: 700,
                          lineHeight: 1.4,
                          letterSpacing: 0.2,
                        }}
                      >
                        {t("undelta_included_title")}
                      </Typography>
                    </Box>
                    <Chip
                      label="HACKATHON 2026"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(250, 204, 21, 0.18)",
                        color: "#FDE68A",
                        border: "1px solid rgba(250, 204, 21, 0.42)",
                        fontWeight: 700,
                        letterSpacing: 0.35,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#e5e5e5",
                      lineHeight: 1.6,
                      fontWeight: 700,
                    }}
                  >
                    {t("undelta_notice")}
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "center", mt: 0 }}>
                    <Button
                      variant="outlined"
                      href="/contact?type=carpinchia"
                      sx={{
                        mt: 2,
                        borderRadius: 999,
                        textTransform: "none",
                        color: "white",
                        borderColor: "#c4b5fd",
                        px: 2,
                        py: 1.4,
                        whiteSpace: "normal",
                        textAlign: "center",
                        fontSize: {
                          xs: "0.85rem",
                          sm: "0.95rem",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                          borderColor: "#a78bfa",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "row",
                          gap: 1.5,
                          textAlign: "left",
                          width: "100%",
                        }}
                      >
                        <RecordVoiceOverOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#c4b5fd" }}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography sx={{ fontSize: "0.85rem" }}>
                            {t("undelta_contact_line1")}
                          </Typography>
                          <Typography sx={{ fontSize: "0.85rem" }}>
                            {t("undelta_contact_line2")}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <Grow in timeout={800}>
            <Box>
              <Typography variant="h5" sx={{ mt: 4, mb: 2, color: "white" }}>
                {t("available_plans")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  mb: 4,
                  mx: {
                    xs: "auto",
                    md: 0,
                  },
                  justifyContent: {
                    xs: "center",
                    md: "flex-start",
                  },
                  alignItems: "stretch",
                }}
              >
                {plans.map(renderPlan)}

                <Box
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.45)",
                    color: "#ffffffd9",
                    borderRadius: 4,
                    boxShadow: 3,
                    maxWidth: 320,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: 4,
                    m: 1,
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                      {t("team_plan_title")}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                      {t("team_plan_text")}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t("team_plan_text2")}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={isUndeltaUser}
                    sx={{ mt: 4, borderRadius: 999 }}
                    href={isUndeltaUser ? undefined : "/contact?type=team_plan"}
                  >
                    {t("team_plan_cta")}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grow>

          <Grow in timeout={900}>
            <Box>
              <Typography id="packs" variant="h5" sx={{ mt: 6, mb: 2, color: "white", scrollMarginTop: "100px" }}>
                {t("extra_sessions")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  textAlign: "center",
                  justifyContent: {
                    xs: "center",
                    md: "flex-start",
                  },
                }}
              >
                {packs.map(renderPack)}
              </Box>
            </Box>
          </Grow>
        </Container>
      </AppBackground>
    </Fade>
  );
};

export default Pricing;
